import React, { useEffect, useState } from "react";
import axios from "axios";
import CustomCalendar from "./CustomCalendar";
import * as XLSX from "xlsx";
import { KTSVG } from "../../../_metronic/helpers";
import { PLreportChart } from "./PLreportChart";
import PlreReportPopUp from "./PlreReportPopUp";
import loadingimage from "../../../Assets/loading-buffering1.gif"
import moment from "moment";


interface Shipment {
    booking_value?: string;
    amount_to_be_paid: string;
    payment_process_status?: string;
    total_discount_given?: string;
    paid_amount?: string;
    split_id: any;
    booking_id: any;
    container_id: string;
    created_at: string;
    total_amount_sum?: number;
    shipment_status: string;
    total_shipment_amount: string
    shipment_jrnyid: string;
}

interface Expense {
    shipment_id: string;
    amount?: number;
    created_at: string;
    expense_name: string;
    comment: string;

}

type ReportType = "shipment" | "direct" | "indirect";

const itemsPerPage = 13; // Number of items per page

const MonthlyReport: React.FC = () => {
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const [shipments, setShipments] = useState<Shipment[]>([]);
    const [directExpenses, setDirectExpenses] = useState<Expense[]>([]);
    const [indirectExpenses, setIndirectExpenses] = useState<Expense[]>([]);
    const [filteredData, setFilteredData] = useState<(Shipment | Expense)[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedType, setSelectedType] = useState<ReportType>("shipment");
    const [selectedData, setSelectedData] = useState<(Shipment | Expense)[]>([]);
    const [isMasterChecked, setIsMasterChecked] = useState(false);
    const [totalSpend, setTotalSpend] = useState<number>(0);
    const [shipmentTotal, setShipmentTotal] = useState<number>(0);
    const [directTotal, setDirectTotal] = useState<number>(0);
    const [indirectTotal, setIndirectTotal] = useState<number>(0);
    const [profit, setProfit] = useState<number>(0);
    const [isPopUpOpen, setIsPopUpOpen] = useState(false);
    const [selectedJourneyId, setSelectedJourneyId] = useState<string | null>(null);
    const [isProfitPopupOpen, setIsProfitPopupOpen] = useState(false);
    const [formattedDate, setFormattedDate] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
      const [progress, setProgress] = React.useState<number>(0);
      const [showProgressBar, setShowProgressBar] = React.useState<boolean>(false);


    const getBadgeClass = (status: string) => {
        switch (status?.trim().toLowerCase()) {
            case "shipment started":
            case "shipment booked":
                return "badge badge-light-info";

            case "collected to shipment":
            case "delivered shipment":
            case "delivered":
                return "badge badge-light-success";

            case "delivered to hub":
            case "shipment progress":
            case "partially delivered": // Fixed issue with extra tab character
                return "badge badge-light-warning";

            case "shipment ended":
            case "pending":
                return "badge badge-light-danger";

            case "main booking":
                return "badge badge-light-primary";

            default:
                return "badge badge-light-secondary";
        }
    };


    useEffect(() => {
        // Set today's date if selectedDate is not set and fetch the report automatically
        if (!selectedDate) {
            const today = new Date();
            setSelectedDate(today);
            fetchMonthlyReport(today); // Automatically fetch the report on page load
        }
    }, []);

    const handleDateChange = (date: Date) => {
        setSelectedDate(date);
        setIsCalendarOpen(false);
        fetchMonthlyReport(date);
    };

    const fetchMonthlyReport = async (date: Date) => {
        setLoading(true);
        const formattedDate = date.toLocaleDateString("en-US", {
            month: "long",
            year: "numeric",
        });

        setFormattedDate(formattedDate);

        const requestBody = {
            created_at: formattedDate,
        };

        try {
            const response = await fetch(
                "https://backendcway.causewaycargo.com/superadmin/plreportshipment",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        auth: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6Nywicm9sZSI6InN1cGVyX2FkbWluIiwiaWF0IjoxNzI2ODM0MzIwfQ.edXyvu0znbVoi5KU3iL8FPww3qe8LMU0oXOCwdZi8do",
                    },
                    body: JSON.stringify(requestBody),
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const result = await response.json();

            if (result.status === "success" && result.data) {
                const { shipments, expenses, total_values } = result.data;

                // Ensure that even if these fields are missing, we reset them to empty
                setShipments(shipments || []);
                setDirectExpenses(expenses?.direct || []);
                setIndirectExpenses(expenses?.indirect || []);

                // Calculate Spend Amount and Profit
                const shipmentTotal = (shipments || []).reduce(
                    (sum: number, item: Shipment) => sum + (Number(item.total_shipment_amount) || 0),
                    0
                );

                const directTotal = (expenses?.direct || []).reduce(
                    (sum: number, item: Expense) => sum + (Number(item.amount) || 0),
                    0
                );

                const indirectTotal = (expenses?.indirect || []).reduce(
                    (sum: number, item: Expense) => sum + (Number(item.amount) || 0),
                    0
                );

                const totalSpend = shipmentTotal + directTotal + indirectTotal;
                const profit = total_values?.netprofit || 0;

                setTotalSpend(parseFloat(totalSpend.toFixed(2)));
                setShipmentTotal(parseFloat(shipmentTotal.toFixed(2)));
                setDirectTotal(parseFloat(directTotal.toFixed(2)));
                setIndirectTotal(parseFloat(indirectTotal.toFixed(2)));
                setProfit(parseFloat(profit.toFixed(2)));
            } else {
                // Reset states if API returns no data
                setShipments([]);
                setDirectExpenses([]);
                setIndirectExpenses([]);
                setTotalSpend(0);
                setShipmentTotal(0);
                setDirectTotal(0);
                setIndirectTotal(0);
                setProfit(0);
            }
        } catch (error) {
            console.error("Error fetching report:", error);
            // Reset states in case of API failure
            setShipments([]);
            setDirectExpenses([]);
            setIndirectExpenses([]);
            setTotalSpend(0);
            setShipmentTotal(0);
            setDirectTotal(0);
            setIndirectTotal(0);
            setProfit(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let data: (Shipment | Expense)[] = [];
        if (selectedType === "shipment") {
            data = shipments.map(({
                container_id,
                created_at,
                total_shipment_amount,
                shipment_jrnyid,
                shipment_status
            }) => ({
                container_id,
                created_at,
                total_shipment_amount,
                shipment_jrnyid,
                shipment_status
            })) as Shipment[];
        } else if (selectedType === "direct") {
            data = directExpenses.map(({ shipment_id, amount, created_at, expense_name, comment }) => ({
                shipment_id,
                amount,
                created_at,
                expense_name,
                comment
            }));
        } else if (selectedType === "indirect") {
            data = indirectExpenses.map(({ shipment_id, amount, created_at, expense_name, comment }) => ({
                shipment_id,
                amount,
                created_at,
                expense_name,
                comment
            }));
        }
        setFilteredData(data);
        setCurrentPage(1);
    }, [selectedType, shipments, directExpenses, indirectExpenses]);

    const currentData = filteredData.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const totalPages = Math.ceil(filteredData.length / itemsPerPage);

    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const handleExportToExcel = () => {
        if (!shipments.length && !directExpenses.length && !indirectExpenses.length) {
            alert("No data available to export.");
            return;
        }
    
        setShowProgressBar(true); // Show progress bar
        setProgress(0); // Reset progress
    
        const startTime = Date.now();
        const estimatedTime = 5000; // 5 seconds to simulate export (you can adjust based on data size)
    
        const intervalId = setInterval(() => {
            const elapsedTime = Date.now() - startTime;
            const progressPercentage = Math.min((elapsedTime / estimatedTime) * 100, 100);
            setProgress(progressPercentage);
    
            if (progressPercentage >= 100) {
                clearInterval(intervalId);
            }
        }, 100); // Update every 100ms
    
        setTimeout(() => {
            const wb = XLSX.utils.book_new();
            const wsData = [];
    
            const addGap = (count = 2) => {
                for (let i = 0; i < count; i++) {
                    wsData.push([]);
                }
            };
    
            // 🟢 Section 1: Shipments
            wsData.push(["Shipments"]);
            wsData.push(["Shipment ID", "Created At", "Shipment Status", "Total Amount"]);
    
            shipments.forEach(item => {
                wsData.push([
                    item?.shipment_jrnyid || "N/A",
                    moment(item?.created_at).format("DD-MM-YYYY") || "N/A",
                    item?.shipment_status === "Shipment Started" ? "In Transit" : item?.shipment_status || "N/A",
                    item?.total_shipment_amount || "0.00",
                ]);
            });
    
            addGap();
    
            // 🟢 Section 2: Direct Expenses
            wsData.push(["Direct Expenses"]);
            wsData.push(["Date", "Name", "Comment", "Amount"]);
    
            directExpenses.forEach(item => {
                wsData.push([
                    moment(item?.created_at).format("DD-MM-YYYY") || "N/A",
                    item?.expense_name || "N/A",
                    item?.comment || "N/A",
                    item?.amount || "0.00",
                ]);
            });
    
            addGap();
    
            // 🟢 Section 3: Indirect Expenses
            wsData.push(["Indirect Expenses"]);
            wsData.push(["Date", "Name", "Comment", "Amount"]);
    
            indirectExpenses.forEach(item => {
                wsData.push([
                    moment(item?.created_at).format("DD-MM-YYYY") || "N/A",
                    item?.expense_name || "N/A",
                    item?.comment || "N/A",
                    item?.amount || "0.00",
                ]);
            });
    
            addGap();
    
            // 🟢 Section 4: Summary
            wsData.push(["Summary"]);
            wsData.push(["Total Shipment", shipmentTotal.toFixed(2)]);
            wsData.push(["Total Direct Expense", directTotal.toFixed(2)]);
            wsData.push(["Total Indirect Expense", indirectTotal.toFixed(2)]);
            wsData.push(["Total Expenses", (directTotal + indirectTotal).toFixed(2)]);
            wsData.push(["Total Profit", profit.toFixed(2)]);
    
            const ws = XLSX.utils.aoa_to_sheet(wsData);
    
            ws["!cols"] = [
                { wch: 20 },
                { wch: 20 },
                { wch: 20 },
                { wch: 20 },
            ];
    
            XLSX.utils.book_append_sheet(wb, ws, "Monthly Report");
    
            const formattedDate = new Date().toLocaleDateString("en-GB").replace(/\//g, "-");
            const fileName = `Monthly_Report_${formattedDate}.xlsx`;
    
            XLSX.writeFile(wb, fileName);
    
            setProgress(100); // Make sure it shows 100% at end
            setShowProgressBar(false); // Hide progress bar after download
        }, estimatedTime);
    };
    

    const handleContainerIdClick = (journeyId: string) => {
        setSelectedJourneyId(null); // Reset first to trigger useEffect again
        setTimeout(() => {
            setSelectedJourneyId(journeyId);
        }, 0); // Ensure re-render
        setIsPopUpOpen(true);
    };

    return (
        <div className="card px-lg-3">
            {showProgressBar && (
        <>
          <div className="modal-backdrop shadow-lg rounded-5">
            <div className="modal d-block" tabIndex={-1} role="dialog">
              <div
                className="modal-dialog modal-dialog-centered"
                role="document"
              >
                <div className="modal-content pb-10 pt-4">
                  <div className="modal-header py-2 d-flex justify-content-end">
                    <div className="modal-body">
                      <h5 className="mb-4">Exporting Data...</h5>
                      <div className="progress" style={{ height: "20px" }}>
                        <div
                          className="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                          role="progressbar"
                          style={{ width: `${progress}%` }}
                          aria-valuenow={progress}
                          aria-valuemin={0}
                          aria-valuemax={100}
                        >
                          {Math.round(progress)}%
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
            <div className="d-flex px-4 card-header gap-4 border-0 pt-5 align-items-center justify-content-between mb-6">
                <div className="d-flex align-items-center gap-4 flex-wrap flex-md-nowrap ">
                    <div className="position-relative">
                        <button
                            className="btn btn-primary"
                            onClick={() => setIsCalendarOpen(!isCalendarOpen)}
                        >
                            {selectedDate
                                ? selectedDate.toLocaleDateString("en-US", { month: "long", year: "numeric" })
                                : "Select Month"}
                        </button>
                        {isCalendarOpen && (
                            <div className="calendar-dropdown position-absolute border rounded">
                                <CustomCalendar
                                    selectedDate={selectedDate}
                                    onDateChange={handleDateChange}
                                    onClose={() => setIsCalendarOpen(false)}
                                />
                            </div>
                        )}
                    </div>

                    <div className="px-4 py-3 gap-3 d-flex btn bg-warning">
                        Total Amount:
                        <div>
                            {selectedType === "shipment" && shipmentTotal !== undefined
                                ? `AED ${new Intl.NumberFormat().format(parseFloat(shipmentTotal.toFixed(0)))}`
                                : selectedType === "direct" && directTotal !== undefined
                                    ? `AED ${new Intl.NumberFormat().format(parseFloat(directTotal.toFixed(0)))}`
                                    : selectedType === "indirect" && indirectTotal !== undefined
                                        ? `AED ${new Intl.NumberFormat().format(parseFloat(indirectTotal.toFixed(0)))}`
                                        : `0.00`
                            }
                        </div>

                        {/* AED ${new Intl.NumberFormat().format(parseFloat(totalSpend.toFixed(0)))} */}
                    </div>

                    <div
                        className="px-4 py-3 btn"
                        style={{
                            color: profit < 0 ? 'red' : 'green',
                            border: `1px solid ${profit < 0 ? 'red' : 'green'}`
                        }}
                        onClick={() => setIsProfitPopupOpen(true)} // Open the modal on click
                    >
                        <span>Total Profit: {`${profit} AED`}</span>
                    </div>

                </div>
                <div className="d-flex align-items-center gap-4 flex-wrap flex-md-nowrap">
                    <select
                        className="form-select"
                        value={selectedType}
                        onChange={(e) => setSelectedType(e.target.value as ReportType)}
                    >
                        <option value="shipment">Shipments</option>
                        <option value="direct">Direct Expenses</option>
                        <option value="indirect">Indirect Expenses</option>
                    </select>
                    <button
                        className="btn btn-danger px-3"
                        style={{ height: "42px", whiteSpace: "nowrap" }}
                        onClick={handleExportToExcel}
                        disabled={!shipments.length && !directExpenses.length && !indirectExpenses.length}
                    >
                        Export to Excel
                    </button>

                </div>

            </div>
            <div className="card-body py-3 d-flex flex-column flex-md-row">
                {loading ? <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '400px' }}>
                    <img src={loadingimage} alt="Loading..." className="w-20" />
                </div> : <div className="table-container flex-grow-1 me-3">
                    <div className="table-responsive">
                        <table className="table table-row-bordered table-row-gray-100 align-middle gs-0 gy-3">
                            <thead>
                                <tr className="fw-bolder text-muted">
                                    {/* <th className="w-25px">
 <input
 type="checkbox"
 checked={isMasterChecked}
 onChange={handleMasterCheckboxChange}
 />
 </th> */}
                                    {selectedType === "shipment" && (
                                        <>
                                            <th className="min-w-120px">Shipment ID</th>
                                            <th className="min-w-120px">Created At</th>
                                            <th className="min-w-120px">Shipment Status</th>
                                            <th className="min-w-120px">Total Amount</th>
                                        </>
                                    )}
                                    {(selectedType === "direct" || selectedType === "indirect") && (
                                        <>
                                            <th className="min-w-120px">Shipment ID</th>
                                            <th className="min-w-120px">Expenses Name</th>
                                            <th className="min-w-120px">Comment</th>
                                            <th className="min-w-120px">Amount</th>
                                            <th className="min-w-120px">Created At</th>
                                        </>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {currentData.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} className="text-center  py-4 text-gray-500">
                                        🚫 No reports available
                                        </td>
                                    </tr>
                                ) : (
                                    currentData.map((item, index) => (
                                        <tr key={index}>
                                            {/* <td>
 <input
 type="checkbox"
 checked={selectedData.includes(item)}
 onChange={(e) => handleCheckboxChange(item, e.target.checked)}
 />
 </td> */}
                                            {"container_id" in item ? (
                                                <>
                                                    <td className='text-dark fw-bolder text-hover-primary'
                                                        style={{ cursor: 'pointer' }}
                                                        onClick={() => handleContainerIdClick(item?.shipment_jrnyid)}
                                                    >{item.shipment_jrnyid}</td>
                                                    <td>{moment(item?.created_at).format('DD-MM-YYYY')}</td>

                                                    <td>
                                                        <span className={`badge ${getBadgeClass(item?.shipment_status || "N/A")}`}>
                                                            {item.shipment_status === "Shipment Started" ? "In Transit" : item.shipment_status}
                                                        </span>
                                                    </td>
                                                    <td>{(item.total_shipment_amount || 0)} QAR</td>
                                                </>
                                            ) : (
                                                <>
                                                    <td className='text-dark fw-bolder text-hover-primary'>{item.shipment_id || "N/A"}</td>
                                                    <td>{item.expense_name || "N/A"}</td>
                                                    <td>{item.comment || "N/A"}</td>
                                                    <td>{(item.amount ?? 0)} QAR</td>
                                                    <td>{moment(item?.created_at).format('DD-MM-YYYY')}</td>
                                                </>
                                            )}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    {filteredData.length > itemsPerPage && (
                        <div className="d-flex justify-content-center mt-3 py-3 mb-3">
                            {/* Previous Page Button */}
                            <button
                                className="btn btn-light-primary me-1 px-4 py-2"
                                disabled={currentPage === 1}
                                onClick={() => handlePageChange(currentPage - 1)}
                            >
                                <KTSVG path="/media/icons/duotune/arrows/arr079.svg" className="svg-icon-3" />
                            </button>

                            {/* Page Numbers */}
                            <div>
                                {Array.from({ length: totalPages }, (_, i) => {
                                    const pageNumber = i + 1;

                                    // Show first page, last page, and pages near the current page
                                    if (
                                        pageNumber === 1 ||
                                        pageNumber === totalPages ||
                                        Math.abs(pageNumber - currentPage) <= 1
                                    ) {
                                        return (
                                            <button
                                                key={pageNumber}
                                                className={`btn btn-light-primary mx-1 px-4 py-2 ${currentPage === pageNumber ? "active" : ""
                                                    }`}
                                                onClick={() => handlePageChange(pageNumber)}
                                            >
                                                {pageNumber}
                                            </button>
                                        );
                                    }

                                    // Ellipsis for gaps
                                    if (
                                        (pageNumber === currentPage - 2 && pageNumber > 1) ||
                                        (pageNumber === currentPage + 2 && pageNumber < totalPages)
                                    ) {
                                        return (
                                            <span key={pageNumber} className="mx-2 text-muted">
                                                ...
                                            </span>
                                        );
                                    }

                                    return null; // Skip rendering other pages
                                })}
                            </div>

                            {/* Next Page Button */}
                            <button
                                className="btn btn-light-primary px-4 py-2 ms-1"
                                disabled={currentPage === totalPages}
                                onClick={() => handlePageChange(currentPage + 1)}
                            >
                                <KTSVG path="/media/icons/duotune/arrows/arr080.svg" className="svg-icon-3" />
                            </button>
                        </div>
                    )}
                </div>}
                {/* <div className="chart-container flex-shrink-0 mt-3 mt-md-0">
 <PLreportChart
 className="card-xxl-stretch mb-5 mb-xl-10"
 totalSpend={totalSpend}
 profit={profit}
 formattedDate={formattedDate} 
 />
 </div> */}

            </div>
            <PlreReportPopUp
                selectedJourneyId={selectedJourneyId}
                isPopUpOpen={isPopUpOpen}
                setIsPopUpOpen={setIsPopUpOpen}
            />

            {isProfitPopupOpen && (
                <div className="modal-backdrop shadow-lg rounded-5">
                    <div className="modal fade show d-block" tabIndex={-1} role="dialog">
                        <div className="modal-dialog modal-dialog-centered modal-xl" role="document">
                            <div className="modal-content pb-3 pt-4">
                                <div className="modal-header py-2 d-flex justify-content-end">
                                    <span className="close-btn" onClick={() => setIsProfitPopupOpen(false)}>
                                        🗙
                                    </span>
                                </div>
                                <div className="modal-body">
                                    {/* Title */}
                                    <h4 className="text-center font-weight-bold mb-3">📊 Profit Report</h4>

                                    {/* Styled Table */}
                                    <table className="table table-striped table-hover text-center">
                                        <tbody>
                                            <tr>
                                                <td className="fw-bold text-gray-600">Total Shipment Amount:</td>
                                                <td className="text-black fw-bold">{shipmentTotal.toLocaleString()} AED</td>
                                            </tr>
                                            <tr>
                                                <td className="fw-bold text-gray-600">Total Expenses:</td>
                                                <td className="text-black fw-bold">{totalSpend.toLocaleString()} AED</td>
                                            </tr>
                                            <tr>
                                                <td className="fw-bold text-gray-600">Direct Expenses:</td>
                                                <td className="text-black fw-bold ">{directTotal.toLocaleString()} AED</td>
                                            </tr>
                                            <tr>
                                                <td className="fw-bold text-gray-600">Indirect Expenses:</td>
                                                <td className="text-black fw-bold">{indirectTotal.toLocaleString()} AED</td>
                                            </tr>
                                            <tr className="border-top">
                                                <td className="fw-bold text-dark">Total Profit:</td>
                                                <td className={`fw-bold ${profit < 0 ? 'text-danger' : 'text-success'}`}>
                                                    {profit.toLocaleString()} AED
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>

                                    {/* Centered Action Button */}
                                    <div className="d-flex justify-content-center mt-4">
                                        <button
                                            className="btn btn-primary px-4 py-2 font-weight-bold"
                                            onClick={() => setIsProfitPopupOpen(false)}
                                        >
                                            Close Report
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}


        </div>
    );
};

export default MonthlyReport;